import type { ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useActiveBrand } from "../../hooks/useActiveBrand";
import { useInboxBadgeCount } from "../../hooks/useInbox";
import {
  buildBrandScopedPath,
  buildSwitchedBrandPath,
  isBrandHubPath,
  isGlobalOperatorPath,
  parseBrandIdFromPath,
} from "../../lib/brand-navigation";
import { matchShellRoute, routeIsInShell } from "../../lib/shell-routes";
import { routeSection } from "../../lib/shell-routes";
import { RailBrandSwitcher } from "./RailBrandSwitcher";

type RailItem = {
  label: string;
  title: string;
  to: string | null;
  disabled?: boolean;
  badge?: string | null;
  active: boolean;
  icon: ReactNode;
};

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { brands, activeBrand, activeBrandId, setActiveBrandId, isLoading, isError } = useActiveBrand();
  const { count: badgeCount, status: badgeStatus } = useInboxBadgeCount(activeBrand?.id);
  const routeBrandId = parseBrandIdFromPath(location.pathname);
  const resolvedBrandId = routeBrandId ?? activeBrand?.id ?? null;
  const brandHref = resolvedBrandId ? `/brand/${resolvedBrandId}/settings/profile` : "/brand";
  const tasksHref = buildBrandScopedPath("/tasks", resolvedBrandId);
  const inboxHref = buildBrandScopedPath("/inbox", resolvedBrandId);
  const activityHref = buildBrandScopedPath("/runs", resolvedBrandId);
  const isDocsActive =
    !!resolvedBrandId &&
    (location.pathname === `/brand/${resolvedBrandId}/docs` ||
      location.pathname.startsWith(`/brand/${resolvedBrandId}/artifacts`));
  const activeSection = routeSection(location.pathname);
  const isWorkflowsActive = activeSection === "workflows";
  const campaignsHref = resolvedBrandId ? `/brand/${encodeURIComponent(resolvedBrandId)}/campaigns` : null;
  const isCampaignsActive = !!campaignsHref && location.pathname.startsWith(campaignsHref);
  const isBrandActive =
    (isBrandHubPath(location.pathname, resolvedBrandId) || isDocsActive) &&
    !isWorkflowsActive && !isCampaignsActive;
  const workflowBadge = badgeStatus === "error"
    ? null
    : badgeCount > 0
      ? (badgeCount > 9 ? "9+" : String(badgeCount))
      : null;

  // The rail switcher must NAVIGATE — ActiveBrandContext.setActiveBrandId no-ops when
  // the chosen id differs from the URL brandId, and brand-scoped pages read brandId
  // from useParams. See memory/feedback_brand_switch_must_navigate.md.
  function handleBrandChange(id: string) {
    const switchedPath = buildSwitchedBrandPath(location.pathname, id);
    if (switchedPath) {
      navigate(switchedPath);
      return;
    }
    if (isGlobalOperatorPath(location.pathname)) {
      navigate(buildBrandScopedPath(`${location.pathname}${location.search}`, id));
      return;
    }
    setActiveBrandId(id);
  }

  // Canonical rail item: icon + mono micro-label, rounded-lg, brand-600 active surround.
  const railItemClass = (active: boolean) =>
    `relative flex w-full flex-col items-center gap-1 rounded-lg px-1 py-2 font-data text-[9px] font-medium uppercase tracking-wide transition-colors ${
      active ? "bg-brand-600/20 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"
    }`;

  const railItems: RailItem[] = [
    { label: "Campaigns", title: "Campaigns", to: campaignsHref, active: isCampaignsActive, icon: <CampaignsIcon /> },
    { label: "Workspace", title: "Workspace home", to: "/home", active: location.pathname === "/home" || matchShellRoute(location.pathname)?.id === "publishing-studio" || ["/outputs/"].some((prefix) => location.pathname.startsWith(prefix) && routeIsInShell(location.pathname)), icon: <HomeIcon /> },
    { label: "Inbox", title: "Inbox", to: inboxHref, active: location.pathname === "/inbox", badge: workflowBadge, icon: <WorkflowsIcon /> },
    { label: "Runs", title: "Runs", to: activityHref, active: location.pathname === "/runs" || location.pathname.startsWith("/runs/"), icon: <RunsIcon /> },
    {
      label: "Actions",
      title: "Actions",
      to: tasksHref,
      active: location.pathname === "/tasks" || location.pathname.startsWith("/tasks/") ||
        (!!resolvedBrandId && (location.pathname === `/brand/${resolvedBrandId}/actions` || location.pathname.startsWith(`/brand/${resolvedBrandId}/actions/`))),
      icon: <TasksIcon />,
    },
  ];

  return (
    <nav aria-label="Primary navigation" className="fixed inset-y-0 left-0 z-30 hidden w-[60px] border-r border-slate-800 bg-slate-900 md:block">
      <div className="flex h-full flex-col items-center gap-2 px-2 py-3">
        {/* Brand switcher lives at the top of the rail — the rail says where you are. */}
        <RailBrandSwitcher
          brands={brands}
          activeBrand={activeBrand}
          activeBrandId={activeBrandId}
          onChange={handleBrandChange}
          isLoading={isLoading}
          isError={isError}
        />
        <div className="mt-1 flex flex-1 flex-col items-stretch gap-1 self-stretch">
          {railItems.map((item) =>
            item.to && !item.disabled ? (
              <NavLink key={item.label} to={item.to} title={item.title} className={railItemClass(item.active)}>
                <span className="relative">
                  {item.icon}
                  {item.badge ? (
                    <span
                      aria-label={`${item.badge} unread item${item.badge === "1" ? "" : "s"}`}
                      className="absolute -right-2 -top-1.5 inline-flex min-w-4 items-center justify-center rounded-full bg-brand-500 px-1 py-0.5 text-[9px] font-semibold text-white"
                    >
                      {item.badge}
                    </span>
                  ) : null}
                </span>
                <span>{item.label}</span>
              </NavLink>
            ) : (
              <span key={item.label} title={item.title} className={`${railItemClass(false)} cursor-default opacity-40`}>
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </span>
            )
          )}
        </div>
        <NavLink to={brandHref} title="Brand profile" className={() => `${railItemClass(isBrandActive)} mt-1`}>
          <BrandIcon />
          <span>Brand</span>
        </NavLink>
        <NavLink to="/settings" title="Account settings" className={({ isActive }) => `${railItemClass(isActive)} mt-1`}>
          <SettingsIcon />
          <span>Settings</span>
        </NavLink>
      </div>
    </nav>
  );
}

function HomeIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3.5 11.5 12 4l8.5 7.5M5.5 10v9a1 1 0 001 1h11a1 1 0 001-1v-9" />
    </svg>
  );
}

function WorkflowsIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4.75 6.75A2.75 2.75 0 017.5 4h9A2.75 2.75 0 0119.25 6.75v10.5A2.75 2.75 0 0116.5 20h-9a2.75 2.75 0 01-2.75-2.75V6.75z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 9h8M8 12.5h5.5M8 16h4" />
    </svg>
  );
}
function RunsIcon() { return <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 4.75h14M5 12h14M5 19.25h14" /></svg>; }

function TasksIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

function BrandIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M10.325 4.317a1 1 0 011.35-.936l.645.292a1 1 0 001.36-.447l.29-.644a1 1 0 011.657-.22l.53.53a1 1 0 01.22 1.657l-.292.645a1 1 0 00.447 1.36l.644.29a1 1 0 01.937 1.35v.75a1 1 0 01-.936 1.35l-.645.292a1 1 0 00-.447 1.36l.292.644a1 1 0 01-.22 1.657l-.53.53a1 1 0 01-1.657.22l-.644-.292a1 1 0 00-1.36.447l-.29.644a1 1 0 01-1.35.937h-.75a1 1 0 01-1.35-.936l-.292-.645a1 1 0 00-1.36-.447l-.644.292a1 1 0 01-1.657-.22l-.53-.53a1 1 0 01-.22-1.657l.292-.644a1 1 0 00-.447-1.36l-.644-.29A1 1 0 013 10.325v-.75a1 1 0 01.936-1.35l.645-.292a1 1 0 00.447-1.36l-.292-.644a1 1 0 01.22-1.657l.53-.53a1 1 0 011.657-.22l.644.292a1 1 0 001.36-.447l.29-.644z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 14.75a2.75 2.75 0 100-5.5 2.75 2.75 0 000 5.5z" />
    </svg>
  );
}

function CampaignsIcon() {
  return <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" /></svg>;
}
